import {Box, Button, Switch, Table, TableBody, TableCell, TableHead, TableRow, Tooltip} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import Stack from '@mui/material/Stack';
import {HOVER_BOX} from '../../../../shared-components/InfoHover';
import {SMALL_BLUE_BUTTON_SX} from '../SettingsPills';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../../shared-components/AdmicityForm';
import useNotification from '../../../../utility/hooks/useNotification';
import {useGetWelfareSettingsQuery, useManageWelfareOptionsMutation} from '../../../../api/services/lookupsService';
import PropTypes from 'prop-types';
import DescriptionSettings from './DescriptionSettings';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';

const WelfareSettings = ({description, onDescriptionChange}) => {
    const {data: welfareOptions = {options: []}, refetch} = useGetWelfareSettingsQuery();
    const [manageWelfareOptions, {isSuccess, isError, error}] = useManageWelfareOptionsMutation();
    const {showSuccessNotification, showErrorNotification} = useNotification();
    const [rows, setRows] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const formRef = useRef(null);
    const [introOpen, setIntroOpen] = useState(false);

    useEffect(() => {
        setRows((welfareOptions.options ?? []).map(option => ({
            id: option.id,
            title: option.title,
            shortDescription: option.shortDescription,
            comment: option.comment,
            hasComment: option.hasComment,
            isOurs: !option.schoolId,
            hidden: !!option.hidden
        })));
    }, [welfareOptions]);

    useEffect(() => {
        if (isSuccess) {
            showSuccessNotification('Welfare questions saved');
            refetch();
        }

        if (isError) {
            showErrorNotification(error?.data?.validationErrors?.[0]?.errorMessage ??
                'Failed to save the welfare questions');
        }
    }, [isSuccess, isError]);

    const move = (from, to) => {
        if (to < 0 || to >= rows.length) {
            return;
        }

        const next = [...rows];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setRows(next);
    };

    const textFields = [
        {
            label: 'Question a parent reads',
            name: 'title',
            required: true,
            multiline: true,
            rows: 3,
            initialValue: selectedRow?.title ?? undefined,
            validate: value => ({
                isValid: Boolean(value && value.trim()) && value.length <= 1000,
                message: !value || !value.trim()
                    ? 'Please write the question as a parent will read it.'
                    : 'A question cannot exceed 1000 characters.'
            })
        },
        {
            label: 'Short name for filters and reports',
            name: 'shortDescription',
            required: true,
            initialValue: selectedRow?.shortDescription ?? undefined,
            validate: value => ({
                isValid: Boolean(value && value.trim()) && value.length <= 100,
                message: !value || !value.trim()
                    ? 'Please give a short name, such as Child in Need.'
                    : 'A short name cannot exceed 100 characters.'
            })
        }
    ];

    // Two questions with the same wording or short name would show as one name in the filters.
    const clash = (data, except) => {
        const key = value => (value ?? '').trim().toLowerCase();
        const others = rows.filter(row => row !== except);

        if (key(data.title) && others.some(row => key(row.title) === key(data.title))) {
            return `"${data.title.trim()}" is already on the list.`;
        }

        if (key(data.shortDescription) && others.some(row => key(row.shortDescription) === key(data.shortDescription))) {
            return `The short name "${data.shortDescription.trim()}" is already used.`;
        }

        return null;
    };

    const handleAdd = data => {
        const problem = clash(data);

        if (problem) {
            showErrorNotification(problem);
            return;
        }

        setRows([...rows, {
            id: null,
            title: data.title,
            shortDescription: data.shortDescription,
            comment: null,
            hasComment: false,
            isOurs: false,
            hidden: false
        }]);
        setOpenAddDialog(false);
    };

    const handleEdit = data => {
        const problem = clash(data, selectedRow);

        if (problem) {
            showErrorNotification(problem);
            return;
        }

        setRows(rows.map(row => row === selectedRow
            ? {...row, title: data.title, shortDescription: data.shortDescription}
            : row));
        setOpenEditDialog(false);
        setSelectedRow(null);
    };

    const handleSave = async () => {
        await manageWelfareOptions({
            options: rows.map(row => ({
                id: row.id,
                shortDescription: row.shortDescription ?? null,
                description: row.title,
                comment: row.comment ?? null,
                hasComment: !!row.hasComment,
                hidden: !!row.hidden
            }))
        });
    };

    // Maria, 20 October 2026: the Welfare questions as a neat table in the style of the other School Settings tables,
    // with the buttons on one row above it and the introductory text in a box opened from that row
    return (
        <Box>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} flexWrap="wrap" sx={{mb: 1}}>
                <Tooltip
                    arrow
                    componentsProps={HOVER_BOX}
                    title="This text appears at the top of the Welfare tab of the student profile."
                >
                    <Button
                        sx={{height: 32, py: 0, px: 1.25, fontSize: 12, backgroundColor: '#fff'}}
                        variant="outlined"
                        data-testid="welfare-intro-text"
                        onClick={() => setIntroOpen(true)}
                    >
                        Introductory text
                    </Button>
                </Tooltip>
                <Button
                    sx={SMALL_BLUE_BUTTON_SX}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    onClick={() => {
                        setSelectedRow(null);
                        setOpenAddDialog(true);
                    }}
                >
                    Add new
                </Button>
                <Button sx={SMALL_BLUE_BUTTON_SX} variant="contained" onClick={handleSave} data-testid="welfare-save">
                    Save
                </Button>
            </Stack>
            <AdmicityDialog
                handleClose={() => setIntroOpen(false)}
                title="Introductory text"
                open={introOpen}
                maxWidth="md"
                actions={[{label: 'Close', onClick: () => setIntroOpen(false)}]}
            >
                <DescriptionSettings
                    fieldLabel={null}
                    rows={4}
                    description={description}
                    defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.welfare]}
                    onDescriptionChange={onDescriptionChange}
                />
            </AdmicityDialog>
            <Box sx={{backgroundColor: '#fff', border: '1px solid #e3e8ef', borderRadius: 2, overflow: 'hidden'}}>
                <Table size="small" data-testid="welfare-questions">
                    <TableHead>
                        <TableRow sx={{'& th': {backgroundColor: '#e3f5f1', fontWeight: 600, py: 0.5, whiteSpace: 'nowrap'}}}>
                            <TableCell sx={{width: 32, px: 0.5}}/>
                            <TableCell>Question</TableCell>
                            <TableCell>Short name</TableCell>
                            <TableCell>Added by</TableCell>
                            <TableCell align="center">In use</TableCell>
                        </TableRow>
                    </TableHead>
                    {/* Maria, 24 October 2026: drag a question by its handle to change the order; no arrows and no
                        Actions column. A question your school added is edited or deleted by clicking on it. */}
                    <DragDropContext onDragEnd={result => result.destination && move(result.source.index, result.destination.index)}>
                        <Droppable droppableId="welfare-questions">
                            {droppable => (
                                <TableBody ref={droppable.innerRef} {...droppable.droppableProps}>
                                    {rows.map((row, index) => (
                                        <Draggable key={`welfare-${row.id ?? row.title}`} draggableId={`welfare-${row.id ?? row.title}`} index={index}>
                                            {(draggable, snapshot) => (
                                                <TableRow
                                                    ref={draggable.innerRef}
                                                    {...draggable.draggableProps}
                                                    data-testid="welfare-question-row"
                                                    sx={{
                                                        height: 32,
                                                        display: snapshot.isDragging ? 'table' : undefined,
                                                        '& td': {backgroundColor: snapshot.isDragging ? '#e3f5f1' : (index % 2 === 1 ? '#f1faf7' : '#fff'), py: 0},
                                                        '& td:not(:nth-of-type(2))': {whiteSpace: 'nowrap'}
                                                    }}
                                                >
                                                    <TableCell sx={{width: 32, px: 0.5}} {...draggable.dragHandleProps} aria-label={`Drag ${row.title} to change the order`}>
                                                        <DragIndicatorIcon fontSize="small" sx={{color: '#9ca3af', display: 'block', cursor: 'grab'}}/>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{opacity: row.hidden ? 0.5 : 1, ...(!row.isOurs && {cursor: 'pointer', color: '#186BBE'})}}
                                                        onClick={() => {
                                                            if (row.isOurs) return;
                                                            setSelectedRow(row);
                                                            setOpenEditDialog(true);
                                                        }}
                                                    >
                                                        {row.title}
                                                    </TableCell>
                                                    <TableCell sx={{opacity: row.hidden ? 0.5 : 1}}>{row.shortDescription ?? '-'}</TableCell>
                                                    <TableCell>{row.isOurs ? 'Admicity' : 'Your school'}</TableCell>
                                                    <TableCell align="center">
                                                        {row.isOurs
                                                            ? (
                                                                <Tooltip title={row.hidden ? 'Turned off' : 'In use'}>
                                                                    <Switch
                                                                        size="small"
                                                                        checked={!row.hidden}
                                                                        onChange={event => setRows(rows.map(r => r === row
                                                                            ? {...r, hidden: !event.target.checked}
                                                                            : r))}
                                                                        inputProps={{'aria-label': `Use ${row.title}`}}
                                                                    />
                                                                </Tooltip>
                                                            )
                                                            : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Draggable>
                                    ))}
                                    {droppable.placeholder}
                                </TableBody>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Table>
            </Box>
            <AdmicityDialog
                handleClose={() => {
                    setOpenAddDialog(false);
                    setOpenEditDialog(false);
                    setSelectedRow(null);
                }}
                title={openAddDialog ? 'New Welfare Question' : 'Update Welfare Question'}
                open={openAddDialog || openEditDialog}
                actions={openEditDialog
                    ? [{
                        label: 'Delete this question',
                        onClick: () => {
                            setRows(rows.filter(r => r !== selectedRow));
                            setOpenEditDialog(false);
                            setSelectedRow(null);
                        }
                    }]
                    : []}
            >
                <AdmicityForm
                    ref={formRef}
                    textFields={textFields}
                    handleSubmit={data => openAddDialog ? handleAdd(data) : handleEdit(data)}
                    buttonText="Save"
                />
            </AdmicityDialog>
        </Box>
    );
};

WelfareSettings.propTypes = {
    description: PropTypes.string,
    onDescriptionChange: PropTypes.func
};

export default WelfareSettings;
